import React, { useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FooterAction,
  Icons,
} from '@ohif/ui-next';

interface DeleteConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasError: boolean;
  isDeleting: boolean;
  onConfirm: () => void;
  onCloseAutoFocus?: (event: Event) => void;
}

/**
 * D5. The inline-error technique of OrganReviewDialog.tsx (a `hasError` banner
 * inside the same DialogContent), not UploadConfirmDialog.tsx's separate
 * dedicated error dialog -- delete has no force/rollback dimension to justify a
 * second dialog. Unlike OrganReviewDialog.tsx this IS a standard modal (default
 * overlay/Esc/outside-click): that dialog's non-modal shape exists only to
 * survive a reviewer scrolling slices behind it, which does not apply here.
 */
export default function DeleteConfirmDialog({
  open,
  onOpenChange,
  hasError,
  isDeleting,
  onConfirm,
  onCloseAutoFocus,
}: DeleteConfirmDialogProps) {
  // Guards the click rather than passing `disabled`, matching
  // OrganReviewDialog.tsx/UploadConfirmDialog.tsx: a native disabled button
  // drops out of the tab order, and FooterAction's onClick is required.
  const confirmIfEnabled = useCallback(() => {
    if (!isDeleting) {
      onConfirm();
    }
  }, [isDeleting, onConfirm]);

  const cancelIfEnabled = useCallback(() => {
    if (!isDeleting) {
      onOpenChange(false);
    }
  }, [isDeleting, onOpenChange]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className="sm:max-w-md"
        data-cy="sycai-delete-confirm-dialog"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader>
          <DialogTitle>Delete study</DialogTitle>
          {/*
            M5.5 D10.1. DialogDescription carries text-base and no colour
            (platform/ui-next/src/components/Dialog/Dialog.tsx, DialogDescription), so an
            uncoloured one inherits the UA default and computes pure black on the dialog
            fill. develop coloured its own equivalent the same way (DeleteDialog.tsx).
          */}
          <DialogDescription className="text-white">
            This permanently deletes the study. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {hasError && (
          <div
            className="text-destructive text-sm"
            data-cy="sycai-delete-error"
          >
            Could not delete this study. Please try again.
          </div>
        )}

        <FooterAction className="gap-3 pt-1">
          {/*
            M5.5 D5. Auxiliary, not Secondary: develop's dismiss carried no fill in either
            state (DeleteDialog.tsx's Cancel), where Secondary resolves to the 40% orange
            wash brand.css's own bg-secondary/85 rule paints.

            The two hover classes replay the other half of that idiom and are not decoration:
            Button.tsx's ghost variant carries hover:bg-primary/25, and cn() is twMerge, so
            hover:bg-transparent DROPS it rather than layering over it. Dimming the label is
            what keeps a hover affordance once the fill is gone -- background alone would
            leave the button with no hover feedback at all.
          */}
          <FooterAction.Auxiliary
            className="hover:text-foreground/60 flex-1 hover:bg-transparent aria-disabled:opacity-50"
            dataCY="sycai-delete-cancel"
            onClick={cancelIfEnabled}
            aria-disabled={isDeleting || undefined}
          >
            Cancel
          </FooterAction.Auxiliary>
          {/*
            M5.5 DL9. develop's confirm was dark red, not brand orange -- bg-red-800
            hover:bg-red-700 on DeleteDialog.tsx's own Delete, measured #9B2C2C to #C53030.
            The ratio is 7.53:1, not stock Tailwind's 8.31:1: this tree replaces the red ramp,
            so red-800 is #9B2C2C and not #991B1B.

            develop paired those classes WITH variant="destructive", and taking the variant
            here would render none of it -- twMerge drops bg-destructive under bg-red-800 --
            while creating the first source-level call site for a token M5.4 D3 deliberately
            left half-finished, its --destructive-foreground unlifted on the grounds that no
            call site existed. The literal alone is what develop's pixels actually were.
          */}
          <FooterAction.Primary
            className="flex-1 bg-red-800 text-white hover:bg-red-700 aria-disabled:opacity-50"
            dataCY="sycai-delete-confirm"
            onClick={confirmIfEnabled}
            aria-disabled={isDeleting || undefined}
          >
            {isDeleting && <Icons.LoadingSpinner className="mr-1" />}
            Delete
          </FooterAction.Primary>
        </FooterAction>
      </DialogContent>
    </Dialog>
  );
}
